'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  COMMON_TIME_ZONES,
  formatOffsetLabel,
  formatZoneDate,
  formatZoneTime,
  localTimeZone,
  parseDateTimeLocal,
  toDateTimeLocalValue,
  wallTimeToInstant,
  zoneLabel,
  zoneOffsetMinutes,
} from '@/lib/browser-time-tools-extra';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const DEFAULT_TARGETS = ['America/New_York', 'Europe/London', 'Asia/Tokyo'];

export default function BrowserTimeZoneConverter() {
  const [ready, setReady] = useState(false);
  const [sourceZone, setSourceZone] = useState('UTC');
  const [dateTime, setDateTime] = useState('');
  const [targets, setTargets] = useState<string[]>(DEFAULT_TARGETS);
  const [addZone, setAddZone] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const local = localTimeZone();
    setSourceZone(local);
    setDateTime(toDateTimeLocalValue(new Date()));
    setReady(true);
  }, []);

  const instant = useMemo(() => {
    const fields = parseDateTimeLocal(dateTime);
    if (!fields) return null;
    return wallTimeToInstant(fields, sourceZone);
  }, [dateTime, sourceZone]);

  const available = useMemo(
    () => COMMON_TIME_ZONES.filter((zone) => !targets.includes(zone.id)),
    [targets],
  );

  function handleAdd() {
    setError(null);
    if (!addZone) return;
    if (targets.includes(addZone)) {
      setError('That time zone is already a target.');
      return;
    }
    setTargets((prev) => [...prev, addZone]);
    setAddZone('');
  }

  function handleRemove(id: string) {
    setTargets((prev) => prev.filter((zone) => zone !== id));
  }

  async function handleCopy() {
    if (!instant) return;
    const lines = targets.map((zone) => {
      const offset = zoneOffsetMinutes(instant, zone);
      return `${zoneLabel(zone)}: ${formatZoneDate(instant, zone)} ${formatZoneTime(instant, zone)} (${formatOffsetLabel(offset)})`;
    });
    try {
      await copyText(lines.join('\n'));
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Time zone converter</span>
        <h2 className="tool-live-title">Time zone converter</h2>
        <p className="tool-live-subtitle">
          Convert a date and time from one zone into many at once. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Source date and time</span>
              <input
                onChange={(event) => setDateTime(event.target.value)}
                type="datetime-local"
                value={dateTime}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Source time zone</span>
              <select onChange={(event) => setSourceZone(event.target.value)} value={sourceZone}>
                {COMMON_TIME_ZONES.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Add a target zone</span>
              <select onChange={(event) => setAddZone(event.target.value)} value={addZone}>
                <option value="">Select a city…</option>
                {available.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!addZone}
              onClick={handleAdd}
              type="button"
            >
              Add target
            </button>
            <button
              className="button button-secondary"
              disabled={!instant}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div style={{ display: 'grid', gap: 12 }}>
            {!ready ? (
              <p className="tool-live-panel-note">Loading…</p>
            ) : !instant ? (
              <p className="status-message" style={{ color: '#dc2626' }}>
                Enter a valid source date and time.
              </p>
            ) : targets.length === 0 ? (
              <p className="tool-live-panel-note">Add at least one target time zone.</p>
            ) : (
              targets.map((zone) => {
                const offset = zoneOffsetMinutes(instant, zone);
                return (
                  <div
                    key={zone}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '12px 14px',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: 12,
                    }}
                  >
                    <div style={{ display: 'grid', gap: 2 }}>
                      <strong style={{ fontSize: 15 }}>{zoneLabel(zone)}</strong>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        {formatZoneDate(instant, zone)} · {formatOffsetLabel(offset)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          fontSize: 22,
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 600,
                        }}
                      >
                        {formatZoneTime(instant, zone)}
                      </span>
                      <button
                        aria-label={`Remove ${zoneLabel(zone)}`}
                        className="button button-secondary"
                        onClick={() => handleRemove(zone)}
                        style={{ padding: '4px 10px' }}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <p className="tool-live-panel-note">
            Conversions use your browser&apos;s time-zone database, including daylight saving.
          </p>
        </section>
      </div>
    </div>
  );
}
